import { computed } from "vue";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import { useMainStore } from "@/store";

/**
 * DCTL-2113: single source of truth for the "show the new per-project dashboard
 * vs. the classic global queue" decision during the project-dashboard rollout.
 *
 * Two LaunchDarkly flags plus one persisted per-user preference:
 *   - `project-dashboard` - the base gate (rolled out to everyone).
 *   - `project-dashboard-legacy-optout` - whether the personal escape hatch is
 *     still honored. Defaults to TRUE so the opt-out is available at launch;
 *     flipping it to false is the hard cutover that forces everyone onto the new
 *     UI (the stored preference then no longer matters, no data migration).
 *   - `user.prefers_legacy_queue` - the user's own opt-out, from /me + login.
 *
 * `enabled` = show the new dashboard =
 *   project-dashboard && !(prefers_legacy_queue && optout-allowed)
 *
 * Must be called from a component `setup()` - `useLDFlag` relies on Vue's inject
 * API. For the router guard (outside setup) read the store directly instead.
 *
 * @returns {{ enabled: import("vue").ComputedRef<boolean>,
 *            showLegacyToggle: import("vue").ComputedRef<boolean>,
 *            prefersLegacyQueue: import("vue").ComputedRef<boolean> }}
 */
export function useProjectDashboardEnabled() {
	const store = useMainStore();
	const projectDashboardFlag = useLDFlag("project-dashboard");
	// Default TRUE: the escape hatch is available until the flag is turned off.
	const optoutAllowed = useLDFlag("project-dashboard-legacy-optout", true);

	const prefersLegacyQueue = computed(() =>
		Boolean(store.user.prefers_legacy_queue),
	);

	const enabled = computed(
		() =>
			Boolean(projectDashboardFlag.value) &&
			!(prefersLegacyQueue.value && Boolean(optoutAllowed.value)),
	);

	// The opt-out control only makes sense while the base flag is on AND the
	// opt-out is still allowed; once the optout flag is off, the toggle vanishes
	// and everyone is on the new UI.
	const showLegacyToggle = computed(
		() => Boolean(projectDashboardFlag.value) && Boolean(optoutAllowed.value),
	);

	return { enabled, showLegacyToggle, prefersLegacyQueue };
}
